import { useRef, useState, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import LoopIcon from "@mui/icons-material/Loop";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { Clipboard } from "@capacitor/clipboard";
import { Capacitor } from "@capacitor/core";

import Layout from "../components/layout/layout";
import { D1, H1, P2, P3 } from "../components/theme/text/Headings";
import { Black, White, Grey, PageGradient } from "../components/theme/colors";
import { useAuth } from "../services/AuthContext";
import RevenueCatService from "../services/RevenueCatService";
import mixpanelService from "../services/MixpanelService";
import postHogService from "../services/PostHogService";
import {
  ProPicGradient,
  SecPicGradient,
} from "../components/theme/ProfilePics";
import {
  Summary,
  ProfileOverviewInsights,
  FriendsModal,
  InsightsText,
} from "../components/profile";
import QuizCompletionBox from "../components/profile/QuizCompletionBox";
import {
  InfoBox,
  FriendBox,
  TraitsContainer,
  TraitItem,
  CopyLinkButton,
} from "../components/profile";

// Styled button matching the Results page regenerate button
const RegenerateButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 10px 16px;
  color: ${Grey};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Profile = () => {
  const { currentUserState } = useAuth();
  const navigate = useNavigate();

  const profileInsightsRef = useRef(null);

  // Hooks for data, traits, and modal
  const {
    profileLoaded,
    username,
    photoURL,
    displayName,
    quizScore,
    friendsCount,
    profileData,
  } = require("../hooks/profile").useProfileData(currentUserState?.uid);

  const { big3, big3Loading } = require("../hooks/profile").useBig3Traits(
    currentUserState?.uid
  );

  const { showFriendsModal, openFriendsModal, closeFriendsModal } =
    require("../hooks/profile").useFriendsModal();

  // State for regenerate functionality
  const [regenerateData, setRegenerateData] = require("react").useState(null);

  // Pro status state
  const [isProUser, setIsProUser] = useState(false);

  // State for copy link functionality
  const [linkCopied, setLinkCopied] = useState(false);

  // Check Pro status on mount
  useEffect(() => {
    const checkProStatus = async () => {
      if (!currentUserState) {
        return;
      }

      try {
        const customerInfo = await RevenueCatService.getCustomerInfo();
        const isPro = RevenueCatService.isProFromCustomerInfo(customerInfo);
        setIsProUser(isPro);
      } catch (error) {
        console.warn("Error checking Pro status:", error);
        setIsProUser(false);
      }
    };

    checkProStatus();
  }, [currentUserState]);

  // Copy profile link to clipboard
  const handleCopyLink = async () => {
    if (!username) {
      console.log("No username available");
      return;
    }

    // Use /p/ prefix for shareable links (shows rich preview in iMessage, then redirects to profile)
    const profileLink = `https://www.vibecheckme.com/p/${username.toLowerCase()}`;
    console.log("Attempting to copy link:", profileLink);

    try {
      // Try Capacitor Clipboard first (works on native and web)
      await Clipboard.write({
        string: profileLink,
      });

      console.log("Link copied successfully!");
      setLinkCopied(true);

      // Track copy link event
      const copyLinkProps = {
        user_id: currentUserState?.uid,
        username: username,
        location: "profile_page",
        is_browser: !Capacitor.isNativePlatform(),
        timestamp: new Date().toISOString(),
      };
      mixpanelService.track("Profile Link Copied", copyLinkProps);
      postHogService.track("Profile Link Copied", copyLinkProps);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
      // Show alert as fallback
      alert(`Copy this link:\n${profileLink}`);
    }
  };

  // Show loading state while data is being fetched
  if (!profileLoaded) {
    return null;
  }

  return (
    <Layout $background={PageGradient} $navbarBackground={Black}>
      {currentUserState !== null && (
        <Container>
          <Row>
            <Col xs={{ span: 3 }} className="ml-15">
              {photoURL ? (
                <ProPicGradient src={photoURL} />
              ) : (
                <SecPicGradient>
                  <D1 style={{ margin: 0, fontSize: 35 }}>
                    {displayName ? displayName[0] : "?"}
                  </D1>
                </SecPicGradient>
              )}
            </Col>
            <Col xs={{ span: 7 }} className="ml-10">
              <InfoBox>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <H1 className="mb-0">
                    {displayName ? displayName : "Your Name"}
                  </H1>
                  {username && (
                    <CopyLinkButton
                      onClick={handleCopyLink}
                      className={linkCopied ? "copied" : ""}
                      title={linkCopied ? "Copied!" : "Copy profile link"}
                    >
                      {linkCopied ? (
                        <CheckIcon style={{ fontSize: 18 }} />
                      ) : (
                        <ContentCopyIcon style={{ fontSize: 18 }} />
                      )}
                    </CopyLinkButton>
                  )}
                </div>
                <P3 className="mt-0 mb-10">
                  {username ? `@${username}` : "no username"}
                </P3>

                <FriendBox>
                  <div
                    className="display-column mb-0 mr-20"
                    role="button"
                    onClick={openFriendsModal}
                  >
                    <H1 className="text-align-center">{friendsCount}</H1>
                    <P2>Friends</P2>
                  </div>
                  <div className="display-column mb-0 ml-10">
                    <H1 className="text-align-center">{quizScore}</H1>
                    <P2>Quiz Score</P2>
                  </div>
                </FriendBox>
              </InfoBox>
            </Col>

            {/* Complete Your Profile prompt - moved from Summary tab */}
            {profileData && (
              <QuizCompletionBox
                userProfile={profileData}
                style={{ margin: "5px 0 25px", padding: "0 25px" }}
              />
            )}

            <InsightsText>
              <P2 bold>{displayName}'s Vibe</P2>
              <ProfileOverviewInsights
                ref={profileInsightsRef}
                userId={currentUserState?.uid}
                displayName={displayName}
                onRegenerateComplete={() => {}}
              />

              {/* Big 3 traits under bio */}
              {big3Loading ? (
                <P2 color={White} className="mt-10" style={{ margin: 0 }}>
                  Loading traits...
                </P2>
              ) : big3 ? (
                (() => {
                  const traits = [
                    big3.personality,
                    big3.political,
                    big3.thinking,
                  ].filter(Boolean);
                  if (traits.length === 0) {
                    return (
                      <P3 color={White} className="mt-10">
                        No traits to show
                      </P3>
                    );
                  }
                  return (
                    <TraitsContainer>
                      {traits.map((trait, index) => {
                        const IconComponent = trait.icon;
                        return (
                          <TraitItem key={index}>
                            <IconComponent
                              style={{ fontSize: 15, color: White }}
                            />
                            <P3 color={White}>{trait.label}</P3>
                          </TraitItem>
                        );
                      })}
                    </TraitsContainer>
                  );
                })()
              ) : null}
            </InsightsText>
          </Row>

          <Summary
            userId={currentUserState?.uid}
            displayName={displayName}
            userProfile={profileData}
            profileInsightsRef={profileInsightsRef}
            onRegenerateDataChange={setRegenerateData}
          />

          {/* Regenerate button at bottom */}
          {regenerateData && regenerateData.completedQuizCount >= 1 && (
            <div
              style={{
                textAlign: "center",
                marginTop: "15px",
                padding: "0 20px",
              }}
            >
              <RegenerateButton
                onClick={() => {
                  if (!isProUser) {
                    // Track paywall hit
                    const paywallHitProps = {
                      feature: "profile_regenerate",
                      location: "profile_page",
                      user_id: currentUserState?.uid,
                      completed_quiz_count: regenerateData.completedQuizCount,
                      timestamp: new Date().toISOString(),
                    };
                    mixpanelService.track(
                      "Paywall Hit - Regenerate",
                      paywallHitProps
                    );
                    postHogService.track(
                      "Paywall Hit - Regenerate",
                      paywallHitProps
                    );

                    // Navigate to Pro page if not a Pro user
                    navigate("/pro");
                  } else {
                    // Track Pro user regeneration
                    const proRegenerateProps = {
                      feature: "profile_regenerate",
                      location: "profile_page",
                      user_id: currentUserState?.uid,
                      is_pro: true,
                      completed_quiz_count: regenerateData.completedQuizCount,
                      timestamp: new Date().toISOString(),
                    };
                    mixpanelService.track(
                      "Pro Feature - Regenerate",
                      proRegenerateProps
                    );
                    postHogService.track(
                      "Pro Feature - Regenerate",
                      proRegenerateProps
                    );

                    // Allow regeneration for Pro users
                    regenerateData.handleRegenerate();
                  }
                }}
                disabled={regenerateData.isRegenerating}
              >
                {regenerateData.isRegenerating ? (
                  "Regenerating..."
                ) : (
                  <>
                    <LoopIcon style={{ fontSize: 17 }} /> Regenerate
                  </>
                )}
              </RegenerateButton>
            </div>
          )}
        </Container>
      )}
      {/* Friends list modal */}
      <FriendsModal
        show={showFriendsModal}
        onClose={closeFriendsModal}
        userId={currentUserState?.uid}
        title="Friends"
      />
    </Layout>
  );
};

export default Profile;
